import { Capacitor } from '@capacitor/core';
import * as Sentry from '@sentry/react';

/**
 * CSP violation telemetry for the native WebView (SHN-2856).
 *
 * The WebView CSP is delivered via a `<meta>` tag in index.html, and the CSP `report-uri`/`report-to`
 * directives are **ignored** when a policy is delivered that way — so there is no server-side report
 * collection for the native app (unlike the CloudFront-served web app, which can use report-uri).
 *
 * Instead we listen for the DOM `securitypolicyviolation` event and forward each violation to Sentry.
 * That gives us production telemetry from real iOS/Android devices: which directive blocked which
 * origin, on which platform — the data needed to tighten or correct the allowlist.
 *
 * See docs/content-security-policy.md.
 */

// Cap noise: a misconfigured directive can fire on every render. We report each unique
// (directive, blocked origin) once per app session and let Sentry aggregate across sessions.
const reportedViolations = new Set<string>();

export interface CspViolationReport {
  blockedURI: string;
  effectiveDirective: string;
  violatedDirective: string;
  disposition: string;
  documentURI: string;
  sourceFile: string;
  lineNumber: number;
  columnNumber: number;
  platform: string;
}

/** Best-effort origin (or scheme) of a blocked URI, used for grouping. Falls back to the raw value. */
function originOf(uri: string): string {
  if (!uri) return '(none)';
  // Keywords like 'inline' / 'eval' are reported verbatim, not as URLs.
  if (!uri.includes('://')) return uri;
  try {
    return new URL(uri).origin;
  } catch {
    return uri;
  }
}

/**
 * Redacts a blocked URI down to origin + pathname, dropping the query string and fragment.
 * Those can carry sensitive data we must not leak into telemetry — e.g. Google Maps URLs include
 * `key=...` and may include user coordinates. Keywords ('inline', 'eval') and unparseable values
 * are returned as-is.
 */
function redactUri(uri: string): string {
  if (!uri) return uri;
  if (!uri.includes('://')) return uri;
  try {
    const { origin, pathname } = new URL(uri);
    return origin + pathname;
  } catch {
    return uri;
  }
}

export function buildCspViolationReport(event: SecurityPolicyViolationEvent): CspViolationReport {
  return {
    // Redacted at the source so the raw query/fragment never reaches Sentry (see redactUri).
    blockedURI: redactUri(event.blockedURI || ''),
    // effectiveDirective is the precise directive; violatedDirective is the legacy alias some engines emit.
    effectiveDirective: event.effectiveDirective || event.violatedDirective || '',
    violatedDirective: event.violatedDirective || '',
    disposition: event.disposition || 'enforce',
    documentURI: event.documentURI || '',
    sourceFile: event.sourceFile || '',
    lineNumber: event.lineNumber || 0,
    columnNumber: event.columnNumber || 0,
    platform: Capacitor.getPlatform(),
  };
}

export function reportCspViolation(event: SecurityPolicyViolationEvent): void {
  const report = buildCspViolationReport(event);
  const blockedOrigin = originOf(report.blockedURI);

  const dedupeKey = `${report.effectiveDirective}|${blockedOrigin}`;
  if (reportedViolations.has(dedupeKey)) return;
  reportedViolations.add(dedupeKey);

  Sentry.captureMessage(`CSP violation: ${report.effectiveDirective} blocked ${blockedOrigin}`, {
    level: 'warning',
    // Group in Sentry by directive + blocked origin rather than per-URL, so one misconfiguration
    // is a single issue instead of thousands.
    fingerprint: ['csp-violation', report.effectiveDirective, blockedOrigin],
    tags: {
      csp_directive: report.effectiveDirective,
      csp_blocked_origin: blockedOrigin,
      csp_disposition: report.disposition,
      platform: report.platform,
    },
    extra: { ...report },
  });
}

/** Test-only: clears the per-session dedupe cache so unit tests stay order-independent. */
export function resetReportedCspViolations(): void {
  reportedViolations.clear();
}

/**
 * Registers the global `securitypolicyviolation` listener. Safe to call once during app boot,
 * ideally after Sentry is initialised (see setup-sentry.ts).
 */
export function registerCspViolationReporter(): void {
  if (typeof document === 'undefined') return;
  document.addEventListener('securitypolicyviolation', reportCspViolation as EventListener);
}
